<template>
  <div class="pa-6">
    <v-card outlined class="pa-6 rounded-xl">
      <span class="labelCard pa-0 px-1 ma-0" :style="`color:${color.primaryVariant}`">
        Estructura de la organización
      </span>
      <v-card-subtitle class="py-0 pb-4" :style="`color:${color.primaryVariant}`">
        Configura las capacidades generales de tu plataforma. Estos ajustes se reflejarán en los menús, formularios y opciones disponibles para los miembros y administradores.
      </v-card-subtitle>

      <v-divider class="mb-6"></v-divider>

      <div class="d-flex flex-column">
        <div class="d-flex align-start justify-space-between mb-6">
          <div class="flex-grow-1 pr-6">
            <div class="wc-subtitle-1 font-weight-bold">
              Mi organización tiene capítulos locales
            </div>
            <div class="wc-body-2 grey--text text--darken-1 mt-1">
              Activa esta opción si cuentas con una oficina matriz y delegaciones o capítulos locales subordinados. Al desactivarla, se ocultarán los menús y opciones relacionadas con capítulos (secciones locales, segmentación por capítulo, etc.).
            </div>
          </div>
          <v-switch
            v-model="hasChapters"
            :loading="loadingChapters"
            :disabled="loadingChapters"
            inset
            color="primary"
            @change="handleHierarchyChange"
          ></v-switch>
        </div>

        <v-divider class="mb-6"></v-divider>

        <div class="d-flex align-start justify-space-between">
          <div class="flex-grow-1 pr-6">
            <div class="wc-subtitle-1 font-weight-bold">
              Mi organización permite submiembros / colaboradores
            </div>
            <div class="wc-body-2 grey--text text--darken-1 mt-1">
              Activa esta opción si los miembros titulares pueden registrar submiembros o colaboradores. Al desactivarla, se ocultará el menú "Mis Submiembros" en el panel del miembro y las opciones para agregar submiembros en toda la plataforma.
            </div>
          </div>
          <v-switch
            v-model="allowSubmembers"
            :loading="loadingSubmembers"
            :disabled="loadingSubmembers"
            inset
            color="primary"
            @change="handleSubmembersChange"
          ></v-switch>
        </div>
      </div>

      <v-alert
        v-if="submemberWarning"
        class="mt-6"
        type="warning"
        dense
        text
      >
        {{ submemberWarning }}
      </v-alert>
    </v-card>

    <v-dialog v-model="confirmChaptersDialog" max-width="480">
      <v-card>
        <v-card-title class="wc-h6">
          {{ hasChapters ? 'Activar capítulos locales' : 'Cambiar a estructura simple' }}
        </v-card-title>
        <v-card-text>
          <template v-if="hasChapters">
            Al activar capítulos locales, los menús y opciones de segmentación por sección local estarán disponibles para los administradores.
          </template>
          <template v-else>
            Para cambiar a una estructura simple, tu organización no debe tener capítulos locales activos. Si los tienes, elimínalos o reasígnalos antes de continuar.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelChaptersChange">Cancelar</v-btn>
          <v-btn
            :loading="loadingChapters"
            :disabled="loadingChapters"
            color="primary"
            @click="confirmChaptersChange"
          >Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmSubmembersDialog" max-width="480">
      <v-card>
        <v-card-title class="wc-h6">
          {{ allowSubmembers ? 'Permitir submiembros' : 'Desactivar submiembros' }}
        </v-card-title>
        <v-card-text>
          <template v-if="allowSubmembers">
            Al permitir submiembros, los miembros titulares podrán registrar colaboradores desde su perfil y verás las opciones de submiembros en los formularios de la plataforma.
          </template>
          <template v-else>
            Al desactivar los submiembros, se ocultará la opción "Mis Submiembros" del menú de los miembros y no se podrán crear nuevos submiembros. Los submiembros existentes no se eliminarán, pero ya no podrás administrarlos desde la plataforma.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelSubmembersChange">Cancelar</v-btn>
          <v-btn
            :loading="loadingSubmembers"
            :disabled="loadingSubmembers"
            color="primary"
            @click="confirmSubmembersChange"
          >Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { colors } from "@/constants/colors.js";
import Endpoints from "@/share/Endpoints.js";

export default {
  name: "OrganizationStructure",
  data() {
    return {
      color: colors,
      hasChapters: false,
      allowSubmembers: true,
      loadingChapters: false,
      loadingSubmembers: false,
      confirmChaptersDialog: false,
      confirmSubmembersDialog: false,
      pendingHierarchy: null,
      pendingAllowSubmembers: null,
      submemberWarning: "",
    };
  },
  computed: {
    ...mapState("user", ["user"]),
  },
  async mounted() {
    this.hasChapters = this.user?.hierarchyType === "TWO_LEVELS";
    this.allowSubmembers = this.user?.allowSubmembers !== false;
    if (this.user?.role !== "HEAD_OFFICE" && this.user?.role !== "ORGANIZATION") {
      this.submemberWarning =
        "Solo los administradores principales de la organización pueden modificar esta configuración.";
    }
  },
  methods: {
    ...mapActions("user", ["realoadUserData"]),
    ...mapMutations("notification", ["show"]),
    isOrganizationAdmin() {
      return ["HEAD_OFFICE", "ORGANIZATION", "ONE_LEVEL_HEAD_OFFICE"].includes(this.user?.role);
    },
    handleHierarchyChange(value) {
      if (!this.isOrganizationAdmin()) {
        this.hasChapters = !value;
        return;
      }
      this.pendingHierarchy = value ? "TWO_LEVELS" : "ONE_LEVEL";
      this.confirmChaptersDialog = true;
    },
    cancelChaptersChange() {
      this.hasChapters = !this.hasChapters;
      this.confirmChaptersDialog = false;
      this.pendingHierarchy = null;
    },
    async confirmChaptersChange() {
      this.loadingChapters = true;
      try {
        const response = await this.axios.put(
          Endpoints.users + "/update-organization-structure",
          { hierarchyType: this.pendingHierarchy }
        );
        if (response.data?.status) {
          await this.realoadUserData({ email: this.user.email });
          this.show({ text: "Configuración de capítulos actualizada", color: "success" });
        } else {
          this.hasChapters = !this.hasChapters;
        }
      } catch (error) {
        this.hasChapters = !this.hasChapters;
        const code = error?.response?.data?.error?.code;
        if (code === "BRANCHES_EXIST") {
          this.show({
            text:
              error.response.data.error.message ||
              "No se puede cambiar la estructura mientras existan capítulos activos.",
            color: "error",
          });
        } else {
          this.show({
            text:
              error?.response?.data?.error?.message || "Error al actualizar la configuración",
            color: "error",
          });
        }
      } finally {
        this.loadingChapters = false;
        this.confirmChaptersDialog = false;
        this.pendingHierarchy = null;
      }
    },
    handleSubmembersChange(value) {
      if (!this.isOrganizationAdmin()) {
        this.allowSubmembers = !value;
        return;
      }
      this.pendingAllowSubmembers = value;
      this.confirmSubmembersDialog = true;
    },
    cancelSubmembersChange() {
      this.allowSubmembers = !this.allowSubmembers;
      this.confirmSubmembersDialog = false;
      this.pendingAllowSubmembers = null;
    },
    async confirmSubmembersChange() {
      this.loadingSubmembers = true;
      try {
        const response = await this.axios.put(
          Endpoints.users + "/update-organization-structure",
          { allowSubmembers: this.pendingAllowSubmembers }
        );
        if (response.data?.status) {
          await this.realoadUserData({ email: this.user.email });
          if (this.pendingAllowSubmembers === true) {
            localStorage.removeItem('wc_cargoPositionNoticeDismissed');
          }
          this.show({ text: "Configuración de submiembros actualizada", color: "success" });
        } else {
          this.allowSubmembers = !this.allowSubmembers;
        }
      } catch (error) {
        this.allowSubmembers = !this.allowSubmembers;
        const code = error?.response?.data?.error?.code;
        if (code === "SUBMEMBERS_EXIST") {
          this.show({
            text:
              error.response.data.error.message ||
              "No puedes desactivar los submiembros mientras tengas submiembros activos.",
            color: "error",
          });
        } else {
          this.show({
            text:
              error?.response?.data?.error?.message || "Error al actualizar la configuración",
            color: "error",
          });
        }
      } finally {
        this.loadingSubmembers = false;
        this.confirmSubmembersDialog = false;
        this.pendingAllowSubmembers = null;
      }
    },
  },
};
</script>

<style scoped>
.labelCard {
  font-size: 1.25rem;
  font-weight: 700;
}
</style>
